/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SERVICES, CONTACT } from "../../lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EstimateForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill in your name, phone and the service you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true);
      toast.success("Request sent! Nick will reach out shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="estimate"
      className="relative bg-[#080D0A] py-28 md:py-40 border-t border-[#223026]"
      data-testid="estimate-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <span className="text-sm uppercase tracking-[0.2em] text-lime-400 font-bold">
            Free Estimate
          </span>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-6xl text-white mt-4 leading-none">
            Let's clear <br /> your land.
          </h2>
          <p className="text-neutral-400 text-base md:text-lg mt-6 max-w-md leading-relaxed">
            Tell us what you're working with and we'll get back to you with a fair,
            no-pressure quote. Prefer to talk? Give us a call.
          </p>

          <div className="mt-10 flex flex-col gap-5">
            <a
              href={CONTACT.phoneHref}
              data-testid="estimate-phone-link"
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 border border-[#223026] flex items-center justify-center text-lime-400 group-hover:bg-lime-400 group-hover:text-black transition-colors duration-200">
                <Phone size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-neutral-500">Call / Text</span>
                <span className="block text-white text-lg font-semibold">{CONTACT.phone}</span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 border border-[#223026] flex items-center justify-center text-lime-400">
                <MapPin size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-neutral-500">Serving</span>
                <span className="block text-white text-lg font-semibold">{CONTACT.location}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full min-h-[400px] flex flex-col items-center justify-center text-center border border-[#223026] bg-[#0F150F] p-12"
              data-testid="estimate-success"
            >
              <CheckCircle2 size={56} className="text-lime-400" />
              <h3 className="font-display font-bold text-3xl text-white mt-6">Request received.</h3>
              <p className="text-neutral-400 mt-3 max-w-sm">
                Thanks for reaching out. {CONTACT.owner} will be in touch soon. Need it
                fast? Call {CONTACT.phone}.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 text-lime-400 text-sm uppercase tracking-wider hover:text-white transition-colors"
                data-testid="estimate-reset-btn"
              >
                Send another request
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              className="border border-[#223026] bg-[#0F150F] p-8 md:p-10 flex flex-col gap-6"
              data-testid="estimate-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-400 mb-2 block">Name</label>
                  <Input
                    data-testid="estimate-name-input"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    className="rounded-none bg-[#141B15] border-[#223026] text-white h-12 focus-visible:ring-1 focus-visible:ring-lime-400"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-400 mb-2 block">Phone</label>
                  <Input
                    data-testid="estimate-phone-input"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(918) 000-0000"
                    className="rounded-none bg-[#141B15] border-[#223026] text-white h-12 focus-visible:ring-1 focus-visible:ring-lime-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-400 mb-2 block">Email <span className="text-neutral-600 normal-case tracking-normal">(optional — for a confirmation)</span></label>
                <Input
                  data-testid="estimate-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@email.com"
                  className="rounded-none bg-[#141B15] border-[#223026] text-white h-12 focus-visible:ring-1 focus-visible:ring-lime-400"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-400 mb-2 block">Service Needed</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    data-testid="estimate-service-select"
                    className="rounded-none bg-[#141B15] border-[#223026] text-white h-12 focus:ring-1 focus:ring-lime-400"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141B15] border-[#223026] text-white rounded-none">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.title}
                        data-testid={`estimate-service-option-${s.id}`}
                        className="focus:bg-lime-400 focus:text-black rounded-none"
                      >
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other" className="focus:bg-lime-400 focus:text-black rounded-none">
                      Other / Not sure
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-400 mb-2 block">Project Details</label>
                <Textarea
                  data-testid="estimate-message-input"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Acreage, location, timeline, what you need done..."
                  rows={4}
                  className="rounded-none bg-[#141B15] border-[#223026] text-white focus-visible:ring-1 focus-visible:ring-lime-400 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="estimate-submit-btn"
                className="rounded-none bg-lime-400 text-black hover:bg-white h-14 text-sm uppercase tracking-wider font-semibold transition-colors"
              >
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Sending...</span>
                ) : (
                  "Request Free Estimate"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
